import React, { useState, useRef } from 'react';

const YouTubeVideoEmbed = ({ 
  videoId, 
  title, 
  description, 
  trackingEvent = 'video_view',
  className = '',
  autoplay = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const iframeRef = useRef(null);

  const handleLoad = () => {
    setIsLoaded(true);
    
    // Track video load
    if (window.gtag) {
      window.gtag('event', `${trackingEvent}_loaded`, {
        'event_category': 'video_engagement',
        'event_label': videoId,
        'video_title': title
      });
    }

    // Facebook Pixel tracking
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_type: 'video',
        content_name: title,
        content_ids: [videoId]
      });
    }
  };

  const handlePlay = () => {
    if (!hasStarted) {
      setHasStarted(true);
      
      // Track video start
      if (window.gtag) {
        window.gtag('event', `${trackingEvent}_start`, {
          'event_category': 'video_engagement',
          'event_label': videoId,
          'video_title': title
        });
      }

      // Facebook Pixel tracking
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_type: 'video_engagement',
          content_name: title
        });
      }

      // LinkedIn Insight Tag
      if (window.lintrk) {
        window.lintrk('track', { conversion_id: 'video_start' });
      }
    }
  };

  const handlePause = () => {
    // Track video pause
    if (window.gtag) {
      window.gtag('event', `${trackingEvent}_pause`, {
        'event_category': 'video_engagement',
        'event_label': videoId,
        'video_title': title
      });
    }
  };

  const handleEnd = () => {
    if (!hasCompleted) {
      setHasCompleted(true);
      
      // Track video completion
      if (window.gtag) {
        window.gtag('event', `${trackingEvent}_complete`, {
          'event_category': 'video_engagement',
          'event_label': videoId,
          'video_title': title
        });
      }

      // Facebook Pixel tracking for high-intent users
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_type: 'video_completion',
          content_name: title
        });
      }

      // LinkedIn Insight Tag
      if (window.lintrk) {
        window.lintrk('track', { conversion_id: 'video_complete' });
      }
    }
  };

  // YouTube API events
  const handleStateChange = (event) => {
    if (event.data === 1) { // Playing
      handlePlay();
    } else if (event.data === 2) { // Paused
      handlePause();
    } else if (event.data === 0) { // Ended
      handleEnd();
    }
  };

  // Add YouTube API listener
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== 'https://www.youtube.com') return;
      
      if (event.data && typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'video-progress') {
            // Track 25%, 50%, 75% milestones
            const progress = data.info.progress;
            if (progress >= 0.25 && progress < 0.5) {
              if (window.gtag) {
                window.gtag('event', `${trackingEvent}_25_percent`, {
                  'event_category': 'video_engagement',
                  'event_label': videoId
                });
              }
            } else if (progress >= 0.5 && progress < 0.75) {
              if (window.gtag) {
                window.gtag('event', `${trackingEvent}_50_percent`, {
                  'event_category': 'video_engagement',
                  'event_label': videoId
                });
              }
            } else if (progress >= 0.75 && progress < 1) {
              if (window.gtag) {
                window.gtag('event', `${trackingEvent}_75_percent`, {
                  'event_category': 'video_engagement',
                  'event_label': videoId
                });
              }
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [trackingEvent, videoId]);

  const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}&rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}`;

  return (
    <div className={`relative ${className}`}>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title={title}
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={handleLoad}
        />
      </div>
      
      {description && (
        <p className="mt-4 text-sm text-gray-600 text-center">
          {description}
        </p>
      )}
      
      {/* Retargeting pixel for video viewers */}
      {isLoaded && (
        <img 
          src={`https://www.facebook.com/tr?id=YOUR_PIXEL_ID&ev=PageView&noscript=1`}
          style={{ display: 'none' }}
          alt=""
        />
      )}
    </div>
  );
};

export default YouTubeVideoEmbed;
